// src/utils/pdfExport.js
import jsPDF from "jspdf";
import QRCode from 'qrcode';

export const exportUnassignedQRCodes = async (qrCodes, toast) => {
  try {
    if (!Array.isArray(qrCodes)) {
      toast?.error("Données QR codes invalides");
      return;
    }

    const unassignedQRCodes = qrCodes.filter(qr => qr?.status === "unassigned");
    
    if (unassignedQRCodes.length === 0) {
      toast?.error("Aucun QR code non assigné à exporter");
      return;
    }

    toast?.loading(`Génération de ${unassignedQRCodes.length} stickers...`, {
      id: "pdf-export"
    });

    // Charger l'image template avec gestion d'erreur
    let templateDataUrl;
    try {
      const templateImg = new Image();
      const templatePaths = [
        '/assets/sticker-template.png',
        '/sticker-template.png',
        '/images/sticker-template.png',
        '/img/sticker-template.png'
      ];
      
      let loaded = false;
      for (const path of templatePaths) {
        try {
          templateImg.src = path;
          await new Promise((resolve, reject) => {
            templateImg.onload = resolve;
            templateImg.onerror = reject;
            setTimeout(() => reject(new Error('Timeout')), 5000);
          });
          loaded = true;
          break;
        } catch (e) {
          console.log(`Chemin ${path} non trouvé`);
        }
      }
      
      if (!loaded) {
        throw new Error("Template image non trouvée");
      }

      const canvas = document.createElement('canvas');
      canvas.width = templateImg.width;
      canvas.height = templateImg.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(templateImg, 0, 0);
      templateDataUrl = canvas.toDataURL('image/png');
      
    } catch (imgError) {
      console.warn("Template non chargé, utilisation du fond blanc:", imgError);
      templateDataUrl = null;
    }

    // Créer le PDF
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4"
    });

    // Configuration A4 (210mm x 297mm)
    const stickersPerRow = 6;
    const stickersPerColumn = 7;
    const stickersPerPage = stickersPerRow * stickersPerColumn; // 40 stickers par page
    
    // Dimensions calculées pour que 8 lignes tiennent sur la page
    const stickerWidth = 26; // mm (fixe pour 5 stickers par ligne)
    const stickerHeight = 38; // mm (réduit pour que 8 lignes tiennent : 33mm * 8 = 264mm, reste 33mm de marges)
    
    // Position du QR code sur le template (ajustée)
    const qrOffsetX = 7; // mm depuis la gauche
    const qrOffsetY = 13.5; // mm depuis le haut (ajusté pour la nouvelle hauteur)
    const qrSize = 12; // mm
    
    // Marges
    const marginX = (210 - (stickerWidth * stickersPerRow)) / 2; // Centrer horizontalement
    const marginY = 15; // Marge haute
    
    // Espacement vertical entre les lignes
    const verticalSpacing = 0; // Pas d'espace supplémentaire entre les stickers

    for (let i = 0; i < unassignedQRCodes.length; i++) {
      const qr = unassignedQRCodes[i];
      
      // Nouvelle page si nécessaire
      if (i > 0 && i % stickersPerPage === 0) {
        pdf.addPage();
      }

      const stickerIndexOnPage = i % stickersPerPage;
      const row = Math.floor(stickerIndexOnPage / stickersPerRow);
      const col = stickerIndexOnPage % stickersPerRow;
      
      // Calcul de la position Y avec espacement contrôlé
      const x = marginX + (col * stickerWidth);
      const y = marginY + (row * (stickerHeight + verticalSpacing));

      // URL complète pour le QR code (version sécurisée)
      const baseUrl = 'https://ultimatag.vercel.app'
      const qrUrl = `${baseUrl}/assign/${qr.qrId}`;
      
      // Générer le QR code en BLEU (#3b82f6)
      const qrDataUrl = await QRCode.toDataURL(qrUrl, {
        width: 300,
        margin: 1,
        color: {
          dark: '#3b82f6', // Bleu demandé
          light: '#FFFFFF'
        }
      });

      // Ajouter le template si disponible
      if (templateDataUrl) {
        pdf.addImage(
          templateDataUrl,
          'PNG',
          x,
          y,
          stickerWidth,
          stickerHeight,
          undefined,
          'FAST'
        );
      } else {
        // Fallback: fond blanc avec bordure bleue
        pdf.setFillColor(255, 255, 255);
        pdf.roundedRect(x, y, stickerWidth, stickerHeight, 2, 2, 'F');
        pdf.setDrawColor(59, 130, 246); // #3b82f6 en RGB
        pdf.setLineWidth(0.3);
        pdf.roundedRect(x, y, stickerWidth, stickerHeight, 2, 2, 'S');
      }

      // Ajouter le QR code bleu à la bonne position
      pdf.addImage(
        qrDataUrl,
        'PNG',
        x + qrOffsetX,
        y + qrOffsetY,
        qrSize,
        qrSize,
        undefined,
        'FAST'
      );

      // Ajouter l'ID du QR code en bas (optionnel)
      pdf.setFontSize(2.5);
      pdf.setTextColor(100, 100, 100);
    }

    const date = new Date().toISOString().split('T')[0];
    pdf.save(`ultimatag-stickers-${date}.pdf`);
    
    toast?.dismiss("pdf-export");
    toast?.success(`${unassignedQRCodes.length} stickers exportés !`);

  } catch (error) {
    console.error("Erreur export PDF:", error);
    toast?.dismiss("pdf-export");
    toast?.error("Erreur: " + error.message);
  }
};